import { inject, Injectable } from '@angular/core';
import { pickBy } from 'es-toolkit/compat';
import { map, Observable, of } from 'rxjs';

import { DailyPlatformInsights, StoredInsightsResponseDto } from '@malou-io/package-dto';
import { CsvAsStringArrays, getDatesBetween, isNotNil, MalouMetric, PlatformKey, StatisticsType } from '@malou-io/package-utils';

import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { InsightsApiService } from '#modules/statistics/services/insights.api.service';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { formatDateToISO, getDayMonthYearFromDate } from '#shared/helpers/index';
import { GMB_METRICS } from '#shared/interfaces/index';

interface Data {
    name: string;
    internalName: string;
    address: string;
    data: DailyPlatformInsights['insights'];
}

type RestaurantInsightsAndDates = { restaurantInsights: Data[]; startDate: Date; endDate: Date } | null;

interface Options {
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedGmbVisibilityCsvInsightsServiceV2 extends AbstractCsvService<RestaurantInsightsAndDates, Options> {
    private readonly _insightsApiService = inject(InsightsApiService);
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected _isDataValid(data: RestaurantInsightsAndDates): boolean {
        return !!data;
    }

    protected override _getData$({ restaurants }: Options): Observable<RestaurantInsightsAndDates> {
        const { endDate, startDate } = this._aggregatedStatisticsStore.datesFilter();

        if (!startDate || !endDate) {
            return of(null);
        }
        return this._insightsApiService
            .getStoredInsights({
                restaurantIds: restaurants.map((r) => r.id),
                platformKeys: [PlatformKey.GMB],
                startDate: getDayMonthYearFromDate(startDate),
                endDate: getDayMonthYearFromDate(endDate),
                metrics: GMB_METRICS.map((m) => m.metric),
                statisticsType: StatisticsType.SEO,
            })
            .pipe(
                map((res) => res.data),
                map((data: StoredInsightsResponseDto) => {
                    const currentInsightsData = pickBy(data, (value) => value?.[PlatformKey.GMB]?.hasData);

                    if (Object.values(currentInsightsData).length === 0 || !startDate || !endDate) {
                        return null;
                    }

                    const restaurantIdsWithData = Object.keys(currentInsightsData);

                    const restaurantInsights: Data[] = restaurantIdsWithData
                        .map((restaurantId) => {
                            const restaurant = restaurants.find((r) => r.id === restaurantId);
                            if (!restaurant) {
                                return null;
                            }
                            return {
                                name: restaurant.name,
                                internalName: restaurant.internalName ?? '',
                                address: restaurant.getFullFormattedAddress().replace(/,/g, ''),
                                data: currentInsightsData[restaurantId]?.[PlatformKey.GMB]?.insights,
                            };
                        })
                        .filter(isNotNil)
                        .sort((a, b) => a.name.localeCompare(b.name));

                    return { restaurantInsights, startDate, endDate };
                })
            );
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return [
            'Date',
            'Location',
            'Location Internal Name',
            'Location Address',
            'Google Maps Impressions Desktop',
            'Google Maps Impressions Mobile',
            'Google Search Impressions Desktop',
            'Google Search Impressions Mobile',
            'Calls',
            'Direction Requests',
            'Website Clicks',
            'Menu Clicks',
            'Booking Clicks',
        ];
    }

    protected override _getCsvDataRows({
        restaurantInsights,
        startDate,
        endDate,
    }: Exclude<RestaurantInsightsAndDates, null>): CsvAsStringArrays {
        const dates = getDatesBetween(startDate, endDate).filter(isNotNil);

        return dates
            .map((date) =>
                restaurantInsights.map((restaurantInsight) => {
                    const dateStr = date.toLocaleDateString();
                    const formattedDay = formatDateToISO(date);

                    const location = restaurantInsight.name;
                    const locationInternalName = restaurantInsight.internalName;
                    const locationAddress = restaurantInsight.address;

                    const businessImpressionsDesktopMaps =
                        restaurantInsight.data?.[MalouMetric.BUSINESS_IMPRESSIONS_DESKTOP_MAPS]?.[formattedDay]?.toString() ?? '';
                    const businessImpressionsMobileMaps =
                        restaurantInsight.data?.[MalouMetric.BUSINESS_IMPRESSIONS_MOBILE_MAPS]?.[formattedDay]?.toString() ?? '';
                    const businessImpressionsDesktopSearch =
                        restaurantInsight.data?.[MalouMetric.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH]?.[formattedDay]?.toString() ?? '';
                    const businessImpressionsMobileSearch =
                        restaurantInsight.data?.[MalouMetric.BUSINESS_IMPRESSIONS_MOBILE_SEARCH]?.[formattedDay]?.toString() ?? '';
                    const actionsPhone = restaurantInsight.data?.[MalouMetric.ACTIONS_PHONE]?.[formattedDay]?.toString() ?? '';
                    const actionsDrivingDirections =
                        restaurantInsight.data?.[MalouMetric.ACTIONS_DRIVING_DIRECTIONS]?.[formattedDay]?.toString() ?? '';
                    const actionsWebsite = restaurantInsight.data?.[MalouMetric.ACTIONS_WEBSITE]?.[formattedDay]?.toString() ?? '';
                    const actionsMenu = restaurantInsight.data?.[MalouMetric.ACTIONS_MENU_CLICK]?.[formattedDay]?.toString() ?? '';
                    const actionsBooking = restaurantInsight.data?.[MalouMetric.ACTIONS_BOOKING_CLICK]?.[formattedDay]?.toString() ?? '';

                    return [
                        dateStr,
                        location,
                        locationInternalName,
                        locationAddress,
                        businessImpressionsDesktopMaps,
                        businessImpressionsMobileMaps,
                        businessImpressionsDesktopSearch,
                        businessImpressionsMobileSearch,
                        actionsPhone,
                        actionsDrivingDirections,
                        actionsWebsite,
                        actionsMenu,
                        actionsBooking,
                    ];
                })
            )
            .flat();
    }
}
